import { useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import { createSolarPotentialFillExpression } from '../../../../molecules/AnalysisMap/utils/energyLayerHelpers'
import { getLayerConfig } from '../../../../molecules/AnalysisMap/config'

interface UseEnergyLayersProps {
  map: React.MutableRefObject<mapboxgl.Map | null>
  mapLoaded: boolean
  popup: React.MutableRefObject<mapboxgl.Popup | null>
  styleLoadedCount: number
  otherMapOptions: any
}

/**
 * Hook to handle energy (solar potential) polygon layers
 * Renders roof sections colored by solar potential
 */
export const useEnergyLayers = ({
  map,
  mapLoaded,
  popup,
  styleLoadedCount,
  otherMapOptions,
}: UseEnergyLayersProps) => {
  // Handle energy polygon layers
  useEffect(() => {
    if (!map.current || !mapLoaded) {
      return
    }

    // Check if energy section is active
    const activeSection = (otherMapOptions as any).activeSection
    const isEnergySection = activeSection === 'energy'
    const roofData = (otherMapOptions as any).sectionData?.roofData
    const layerStates = (otherMapOptions as any).demographicsLayerStates // Reuse same prop for consistency

    const layerId = 'energy-solar-potential'
    const sourceId = `${layerId}-source`

    if (
      !isEnergySection ||
      !roofData ||
      !roofData.features ||
      roofData.features.length === 0
    ) {
      // Remove energy layers if not in energy section
      if (map.current!.getLayer(layerId)) {
        try {
          map.current!.removeLayer(layerId)
        } catch (error) {
          console.warn(`Failed to remove energy layer ${layerId}:`, error)
        }
      }

      const outlineLayerId = `${layerId}-outline`
      if (map.current!.getLayer(outlineLayerId)) {
        try {
          map.current!.removeLayer(outlineLayerId)
        } catch (error) {
          console.warn(`Failed to remove energy outline layer:`, error)
        }
      }

      if (map.current!.getSource(sourceId)) {
        try {
          map.current!.removeSource(sourceId)
        } catch (error) {
          console.warn(`Failed to remove energy source ${sourceId}:`, error)
        }
      }

      return
    }

    // Energy section is active - create solar potential layer
    const layerConfig = getLayerConfig(layerId)
    const layerState = layerStates?.[layerId] || {
      enabled: layerConfig?.defaultEnabled !== false,
      opacity: layerConfig?.defaultOpacity || 60,
    }

    // Add or update GeoJSON source
    if (!map.current!.getSource(sourceId)) {
      map.current!.addSource(sourceId, {
        type: 'geojson',
        data: roofData,
        generateId: true, // Generate IDs for feature-state
      })
    } else {
      // Update existing source data
      const source = map.current!.getSource(sourceId) as mapboxgl.GeoJSONSource
      source.setData(roofData)
    }

    // Add or update polygon fill layer
    if (!map.current!.getLayer(layerId)) {
      map.current!.addLayer({
        id: layerId,
        type: 'fill',
        source: sourceId,
        layout: {
          visibility: layerState.enabled ? 'visible' : 'none',
        },
        paint: {
          'fill-color': createSolarPotentialFillExpression(),
          'fill-opacity': layerState.opacity / 100,
        },
      })

      // Add outline layer for better visibility
      const outlineLayerId = `${layerId}-outline`
      if (!map.current!.getLayer(outlineLayerId)) {
        map.current!.addLayer({
          id: outlineLayerId,
          type: 'line',
          source: sourceId,
          layout: {
            visibility: layerState.enabled ? 'visible' : 'none',
          },
          paint: {
            'line-color': '#000000',
            'line-width': 1,
            'line-opacity': 0.3,
          },
        })
      }
    } else {
      // Update existing layer
      map.current!.setLayoutProperty(
        layerId,
        'visibility',
        layerState.enabled ? 'visible' : 'none'
      )
      map.current!.setPaintProperty(
        layerId,
        'fill-opacity',
        layerState.opacity / 100
      )

      // Update outline layer
      const outlineLayerId = `${layerId}-outline`
      if (map.current!.getLayer(outlineLayerId)) {
        map.current!.setLayoutProperty(
          outlineLayerId,
          'visibility',
          layerState.enabled ? 'visible' : 'none'
        )
      }
    }
  }, [mapLoaded, styleLoadedCount, otherMapOptions])

  // Setup hover tooltips for energy layer
  useEffect(() => {
    if (!map.current || !mapLoaded) return

    const activeSection = otherMapOptions.activeSection
    const roofData = otherMapOptions.sectionData?.roofData
    const layerStates = otherMapOptions.demographicsLayerStates

    const layerId = 'energy-solar-potential'
    const layerState = layerStates?.[layerId]
    const isEnabled = layerState?.enabled ?? false
    const layerExists = !!map.current!.getLayer(layerId)

    if (
      activeSection !== 'energy' ||
      !roofData ||
      !roofData.features ||
      roofData.features.length === 0 ||
      !isEnabled ||
      !layerExists
    ) {
      return
    }

    // Handle mouse move to show tooltip
    const handleMouseMove = (e: mapboxgl.MapMouseEvent) => {
      const features = map.current!.queryRenderedFeatures(e.point, {
        layers: [layerId],
      })

      if (features.length > 0 && popup.current) {
        const feature = features[0]
        const exposure = feature.properties?.average_exposure

        if (exposure !== undefined) {
          // Determine solar potential category
          let category = ''
          let color = ''
          if (exposure >= 1400) {
            category = 'Excellent'
            color = '#A80200'
          } else if (exposure >= 1200) {
            category = 'Very Good'
            color = '#FA5502'
          } else if (exposure >= 1000) {
            category = 'Good'
            color = '#FCAA00'
          } else if (exposure >= 800) {
            category = 'Moderate'
            color = '#FFFC09'
          } else {
            category = 'Poor'
            color = '#2DC5FF'
          }

          // Change cursor to pointer
          map.current!.getCanvas().style.cursor = 'pointer'

          // Show popup with solar potential info
          popup.current
            .setLngLat(e.lngLat)
            .setHTML(
              `<div style="padding: 8px 12px; font-size: 13px;">
                <div style="margin-bottom: 4px;">
                  <strong>Solar Potential</strong>
                </div>
                <div style="display: flex; align-items: center; gap: 8px;">
                  <div style="width: 12px; height: 12px; background-color: ${color}; border-radius: 2px;"></div>
                  <span>${category}</span>
                </div>
                <div style="margin-top: 4px; color: #666; font-size: 12px;">
                  ${exposure} kWh/m²/year
                </div>
              </div>`
            )
            .addTo(map.current!)
        }
      } else {
        // Reset cursor
        map.current!.getCanvas().style.cursor = ''
        popup.current?.remove()
      }
    }

    // Handle mouse leave to hide tooltip
    const handleMouseLeave = () => {
      map.current!.getCanvas().style.cursor = ''
      popup.current?.remove()
    }

    // Add event listeners
    map.current!.on('mousemove', layerId, handleMouseMove)
    map.current!.on('mouseleave', layerId, handleMouseLeave)

    // Cleanup
    return () => {
      if (map.current) {
        map.current!.off('mousemove', layerId, handleMouseMove)
        map.current!.off('mouseleave', layerId, handleMouseLeave)
      }
      popup.current?.remove()
    }
  }, [
    mapLoaded,
    styleLoadedCount,
    otherMapOptions.sectionData,
    otherMapOptions.demographicsLayerStates,
    otherMapOptions.activeSection,
  ])
}
